/** 站内路由前缀，与 router/index.js 保持一致 */
const INTERNAL_PATH_PREFIXES = [
  '/solutions',
  '/news',
  '/about',
  '/contact',
  '/cases',
  '/admin'
]

function isAbsoluteUrl(link) {
  return /^https?:\/\//i.test(link || '')
}

function getInternalHosts() {
  const hosts = []
  const configured = process.env.VUE_APP_INTERNAL_HOSTS

  if (configured) {
    hosts.push(...configured.split(',').map(host => host.trim()).filter(Boolean))
  }

  ;[process.env.VUE_APP_ANQICMS_API_BASE, process.env.VUE_APP_ANQICMS_PROXY_TARGET].forEach(base => {
    if (!base) return
    try {
      hosts.push(new URL(base).hostname)
    } catch (e) {
      // ignore invalid url
    }
  })

  if (typeof window !== 'undefined' && window.location.hostname) {
    hosts.push(window.location.hostname)
  }

  return [...new Set(hosts.filter(Boolean))]
}

function isLocalHost(hostname) {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]'
}

function isIpAddress(hostname) {
  if (!hostname) return false
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) return true
  return hostname.startsWith('[') && hostname.endsWith(']')
}

function normalizePathname(pathname) {
  if (!pathname || pathname === '/' || pathname === '/index.html' || pathname === '/index.htm') {
    return '/'
  }
  return pathname
}

function isInternalSiteHost(hostname) {
  return isKnownInternalHost(hostname) || isLocalHost(hostname) || isIpAddress(hostname)
}

function isKnownInternalHost(hostname) {
  if (!hostname) return false
  return getInternalHosts().some(host => hostname === host || hostname.endsWith(`.${host}`))
}

export function isInternalPath(pathname) {
  if (!pathname || pathname === '/') return false
  return INTERNAL_PATH_PREFIXES.some(
    prefix => pathname === prefix || pathname.startsWith(`${prefix}/`)
  )
}

function resolveAbsoluteUrl(url) {
  const pathname = normalizePathname(url.pathname)
  const path = pathname === '/'
    ? '/'
    : `${pathname}${url.search}${url.hash}` || '/'

  if (isInternalPath(pathname)) {
    return { path, href: url.href, external: false }
  }

  if (pathname === '/' && !url.search && isInternalSiteHost(url.hostname)) {
    return { path: '/', href: url.href, external: false }
  }

  return { path: url.href, href: url.href, external: true }
}

/**
 * 解析链接：完整 URL 提取路由部分用于站内切换，站外链接标记为新窗口打开
 * @returns {{ path: string, href: string, external: boolean }}
 */
export function parseNavLink(link) {
  const trimmed = (link || '').trim()
  if (!trimmed) {
    return { path: '/', href: '/', external: false }
  }

  if (isAbsoluteUrl(trimmed)) {
    try {
      return resolveAbsoluteUrl(new URL(trimmed))
    } catch (e) {
      return { path: trimmed, href: trimmed, external: true }
    }
  }

  const normalized = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
  return { path: normalized, href: normalized, external: false }
}

export const HEADER_SCROLL_OFFSET = 80

export function splitNavPath(path) {
  const raw = (path || '/').split('?')[0]
  const hashIndex = raw.indexOf('#')
  if (hashIndex === -1) {
    return { path: raw || '/', hash: '' }
  }
  return {
    path: raw.slice(0, hashIndex) || '/',
    hash: raw.slice(hashIndex)
  }
}

/** 将带 hash 的路径转为 vue-router location 对象 */
export function parseNavRouteTo(path) {
  const { path: pathname, hash } = splitNavPath(path)
  if (hash) {
    return { path: pathname, hash }
  }
  return pathname || '/'
}

/** 滚动到 hash 锚点，支持异步渲染后重试 */
export function scrollToHash(hash, options = {}) {
  const id = (hash || '').replace(/^#/, '')
  if (!id) return Promise.resolve(false)

  const offset = options.offset ?? HEADER_SCROLL_OFFSET
  const maxAttempts = options.maxAttempts ?? 30
  const interval = options.interval ?? 100
  const behavior = options.behavior || 'smooth'

  return new Promise(resolve => {
    let attempt = 0

    const tryScroll = () => {
      const el = document.getElementById(id)
      if (el) {
        const top = el.getBoundingClientRect().top + window.pageYOffset - offset
        window.scrollTo({ top, behavior })
        resolve(true)
        return
      }

      attempt += 1
      if (attempt >= maxAttempts) {
        resolve(false)
        return
      }

      setTimeout(tryScroll, interval)
    }

    tryScroll()
  })
}

export function getNavLinkKey(link) {
  const path = typeof link === 'string' ? link : (link.path || link.href || '/')
  const { path: pathname, hash } = splitNavPath(path)
  return `${pathname}${hash}`
}

/** 二级菜单精确匹配：仅 pathname + hash 完全一致时视为当前页 */
export function isNavLinkActive(linkPath, route) {
  if (!route) return false

  const { path: targetPath, hash: targetHash } = splitNavPath(linkPath)
  const currentPath = route.path || '/'
  const currentHash = route.hash || ''

  if (targetHash) {
    return currentPath === targetPath && currentHash === targetHash
  }

  return currentPath === targetPath && !currentHash
}

/**
 * 下拉子项中仅唯一匹配时才高亮，避免多个相同链接全部选中
 */
export function getUniqueActiveNavKey(items, route) {
  if (!Array.isArray(items) || !items.length || !route) return null

  const matched = items.filter(
    item => !item.disabled && item.path && isNavLinkActive(item.path, route)
  )
  if (matched.length !== 1) return null

  return getNavLinkKey(matched[0])
}

/**
 * 统一跳转：站内走路由切换，站外或指定新窗口时用 window.open
 * @param {import('vue-router').default} router
 * @param {string|{ path: string, href?: string, external?: boolean }} link
 * @param {{ target?: '_self'|'_blank' }} [options]
 */
export function navigateTo(router, link, options = {}) {
  const info = typeof link === 'string' ? parseNavLink(link) : link
  const { path, href, external } = info
  const openInNewTab = options.target === '_blank' || external

  if (openInNewTab) {
    window.open(href || path, '_blank', 'noopener,noreferrer')
    return Promise.resolve()
  }

  if (router) {
    const routeTo = parseNavRouteTo(path)
    return router.push(routeTo).catch(err => {
      if (err && err.name !== 'NavigationDuplicated') {
        throw err
      }
    })
  }

  window.location.href = path
  return Promise.resolve()
}
